import { apiCall } from '../../services/apiUtil';

/**
 * An entity filter onto a query string.
 *
 * A tournament, program or academy becomes `<type>Id=<id>`. The unlinked group
 * has no id — it is the lines that belong to none of them — so it becomes a
 * flag instead. Shared by the ledger and the export so the two cannot disagree
 * about what the same selection means.
 */
const applyEntity = (q, entity) => {
  if (!entity?.type) return;
  if (entity.id) q.set(`${entity.type}Id`, entity.id);
  else if (entity.type === 'none') q.set('unlinked', 'true');
};

/**
 * Card payment endpoints. Deliberately thin: the server owns the amount, who
 * is allowed to pay, and what counts as paid — the client only says which
 * registrations it wants to settle and where it should be returned to.
 */
export const paymentsAPI = {
  /** Whether this deployment can take card at all. */
  getConfig: () => apiCall('/payments/config'),

  /**
   * What a basket would cost, priced by the server. Callers name subjects —
   * never amounts, never price ids — so the total shown is the total charged.
   * @param {{items: {subjectType: string, subjectId: string, quantity?: number}[]}} body
   */
  quoteBasket: (body) => apiCall('/checkout/quote', {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  /** One order, with its lines and current payment state. */
  getOrder: (orderId) => apiCall(`/orders/${orderId}`),

  /** Everything this player owes or has bought, newest first, across venues. */
  myOrders: (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    ).toString();
    return apiCall(`/payments/my-orders${q ? `?${q}` : ''}`);
  },

  /**
   * The live order this player already has against a subject, if any.
   *
   * Registration is idempotent, so re-submitting an entry that already exists
   * returns no new order id — there was nothing new to raise. Anything that then
   * wants to PAY has to find the order that already exists, or it silently does
   * nothing and the payer is left looking at a form that appeared to work.
   */
  liveOrderFor: async (subjectType, subjectId) => {
    const { orders = [] } = await apiCall('/payments/my-orders?limit=100');
    return orders.find(o => o.status === 'pending'
      && (o.items || []).some(i => i.subjectType === subjectType
                                && String(i.subjectId) === String(subjectId))) || null;
  },

  /**
   * START A BOOKING — the one way in, whatever is being bought.
   *
   * Holds whatever has to exist before the money (a court's event, a session's
   * seat) and raises the order for it, both in one transaction on the server.
   *
   * The browser does not raise orders itself. It used to call POST /orders
   * directly for programmes and tournaments while courts and sessions went
   * through their own booking endpoints — so which call to make depended on
   * what was being bought, and only two of the four held anything first.
   *
   * A court is asked for by COURT and TIME, because the thing actually sold is
   * the booking made from them and its id does not exist yet. Everything else
   * names its subject directly. The order that comes back names whatever was
   * held.
   */
  startBooking: async (body) => {
    const res = await apiCall('/bookings', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    /* Hands back the ORDER, as raising one used to.
     *
     * The endpoint answers `{ order, held, reused }` — `held` naming whatever
     * had to exist before the money, which the server needed to tell itself and
     * no screen has a use for. Unwrapping here keeps every caller reading the
     * same thing it always did.
     *
     * ⚠️ `reused` RIDES ALONG, because unwrapping would otherwise drop the one
     * fact that decides whether the order is the caller's to throw away. One
     * live order per person per thing means Book on something already booked
     * hands back the EXISTING order, and checkout withdraws the order when the
     * payer backs out — so losing this flag here is a cancelled booking two
     * screens later. */
    if (!res?.order) return res;
    return Object.assign(res.order, { reused: res.reused === true });
  },

  /**
   * How the payer says they will settle, recorded on a still-pending order.
   *
   * Never marks anything paid. It exists so the desk can tell "they said they
   * would bring cash" from "they opened a card payment and never came back" —
   * two rows that otherwise look identical and need different chasing.
   */
  setOrderIntent: (orderId, paymentMethod) => apiCall(`/orders/${orderId}/intent`, {
    method: 'PATCH',
    body: JSON.stringify({ paymentMethod }),
  }),

  /**
   * An entry whose charge was pulled but whose place was never given up.
   * Either bill it again — a NEW order, so the trail keeps saying what happened
   * — or take the place back. Both need the desk capability.
   */
  rebillOrphanedEntry: (orderId) => apiCall(`/orders/${orderId}/rebill`, { method: 'POST' }),

  releaseOrphanedEntry: (orderId, body) => apiCall(`/orders/${orderId}/release`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  /** Staff confirming money taken at the counter. Needs manage_programs. */
  markOrderPaid: (orderId, body) => apiCall(`/orders/${orderId}/mark-paid`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  /**
   * Finish an order that costs nothing — no payment step, no Stripe.
   *
   * The payer calls this for their own order. The server reads the total off
   * the row and refuses anything that is not zero, so it cannot be used to
   * take a place that has something to pay.
   */
  completeFreeOrder: (orderId) => apiCall(`/orders/${orderId}/complete-free`, {
    method: 'POST',
  }),

  /**
   * Give a settled order's money back, in full. Reverses the Stripe charge for a
   * card payment; recorded only for cash, Venmo and the venue's own terminal.
   * Needs manage_programs.
   */
  refundOrder: (orderId, body = {}) => apiCall('/payments/refunds', {
    method: 'POST',
    // The order is named in the BODY: this is not an order endpoint. Nothing in
    // the UI acts on the order API, and a screen that could POST to /orders/:id
    // could be pointed at any order id somebody types.
    body: JSON.stringify({ orderId, ...body }),
  }),

  /**
   * Withdraw an unpaid order and release any place it was holding. Refused for a
   * paid order — that is a refund. Needs manage_programs.
   */
  cancelOrder: (orderId, body = {}) => apiCall(`/orders/${orderId}/cancel`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  /**
   * What one academy is owed, and what it has taken — the front desk's list.
   * Returns each order with the payer and the players it was bought for, plus
   * `owed` across the pending ones. Needs manage_programs.
   * @param {{status?: 'pending'|'paid'|'all', from?: string, to?: string, limit?: number, userId?: string}} [params]
   */
  academyOrders: (academyId, params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, v);
    });
    const qs = q.toString();
    return apiCall(`/academies/${academyId}/orders${qs ? `?${qs}` : ''}`);
  },

  /**
   * People this academy has money records for, by name, email or phone — or by
   * id, which is what a scanned QR code gives. The desk's search box.
   * @param {{q?: string, userId?: string, limit?: number}} [params]
   */
  academyPayers: (academyId, params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, v);
    });
    const qs = q.toString();
    return apiCall(`/academies/${academyId}/payers${qs ? `?${qs}` : ''}`);
  },

  /**
   * @param {'program'|'tournament'|'order'} kind
   * @param {(number|string)[]} registrationIds  ids for the registration kinds,
   *        order UUIDs for 'order'
   * @param {string} returnPath  path within the app, e.g. "/tournament/79?tab=enrolled"
   * @param {string} [cancelPath]
   */
  createCheckoutSession: ({ kind, registrationIds, returnPath, cancelPath, uiMode }) =>
    apiCall('/payments/checkout-session', {
      method: 'POST',
      // uiMode is omitted rather than sent as undefined: the route schema pins
      // it to an enum, and an explicit undefined would be a validation error
      // where an absent key correctly falls back to the hosted redirect.
      body: JSON.stringify({ kind, registrationIds, returnPath, cancelPath, ...(uiMode ? { uiMode } : {}) }),
    }),

  /**
   * The extra questions this offering asks, and who is asking.
   *
   * One call for anything sellable — a programme, a session, a court, a
   * tournament — because the questions are keyed by the same subject pair the
   * order already carries. Checkout used to list every programme an academy
   * runs and pick one out of it.
   */
  offeringQuestions: (subjectType, subjectId) =>
    apiCall(`/offerings/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}/questions`),

  /** Save what checkout collected onto the order, before any money moves. */
  updateOrderPayload: (orderId, patch) =>
    apiCall(`/orders/${orderId}/payload`, { method: 'PATCH', body: JSON.stringify(patch) }),

  /** What must be signed before this offering can be bought. */
  offeringAgreements: (subjectType, subjectId) =>
    apiCall(`/offerings/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}/agreements`),

  /** Record a signature against the person the place is for. */
  acceptOfferingAgreements: (subjectType, subjectId, body) =>
    apiCall(`/offerings/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}/agreements`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Who already holds a place, so they can be ruled out before anyone chooses.
   *
   * `orderId` is not optional in practice, it is the whole difficulty: the
   * place is held before checkout asks who it is for, so without it the payer
   * comes back already booked on their own order and is greyed out of it. The
   * server discounts only what THIS order is holding — somebody who booked last
   * week is still reported.
   */
  offeringRegistrants: (subjectType, subjectId, orderId = null) =>
    apiCall(`/offerings/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}/registrants`
      + (orderId ? `?orderId=${encodeURIComponent(orderId)}` : '')),

  /** Verifies with Stripe and settles the registrations the session paid for. */
  getCheckoutStatus: (sessionId) =>
    apiCall(`/payments/checkout-session/${encodeURIComponent(sessionId)}`),

  // ── Unpaid card registrations that got stuck ─────────────────────────────

  /**
   * Ask Stripe what actually happened to registrations that read unpaid.
   *
   * Batched deliberately: pass every stuck row at once. Outcome per
   * registration is `already_paid`, `no_session`, `settled`, `unpaid` or
   * `review`, and it is what everything else branches on — offering to pay
   * again before knowing this is how a card gets charged twice.
   *
   * @param {'program'|'tournament'} kind
   * @param {number[]} registrationIds
   */
  checkRegistrations: ({ kind, registrationIds }) =>
    apiCall('/payments/registrations/check', {
      method: 'POST',
      body: JSON.stringify({ kind, registrationIds }),
    }),

  /**
   * Give up on the card and pay the organiser directly. Lands in
   * `pending_confirmation`, never `paid` — a coach still confirms receipt.
   *
   * @param {'cash'|'venmo'} paymentMethod
   */
  switchPaymentMethod: ({ kind, registrationId, paymentMethod }) =>
    apiCall('/payments/registrations/payment-method', {
      method: 'POST',
      body: JSON.stringify({ kind, registrationId, paymentMethod }),
    }),

  /** Record that money left an account for a registration that reads unpaid. */
  raisePaymentDispute: ({ kind, registrationId, message }) =>
    apiCall('/payments/registrations/disputes', {
      method: 'POST',
      body: JSON.stringify({ kind, registrationId, message }),
    }),

  // ── Connecting a host's own Stripe account (optional) ────────────────────

  getConnectStatus: (ownerType, ownerId) =>
    apiCall(`/payments/connect/status?ownerType=${ownerType}&ownerId=${encodeURIComponent(ownerId)}`),

  /** Returns a single-use Stripe-hosted onboarding URL to redirect to. */
  startConnectOnboarding: ({ ownerType, ownerId, returnPath }) =>
    apiCall('/payments/connect/onboard', {
      method: 'POST',
      body: JSON.stringify({ ownerType, ownerId, returnPath }),
    }),

  /** One-time link into the host's own Stripe Express dashboard. */
  getConnectDashboardLink: (ownerType, ownerId) =>
    apiCall('/payments/connect/dashboard-link', {
      method: 'POST',
      body: JSON.stringify({ ownerType, ownerId }),
    }),

  // ── The platform fee agreement ──────────────────────────────────────────

  /** The contract this host would sign, rendered server-side, or the signed one. */
  getFeeAgreement: (ownerType, ownerId) =>
    apiCall(`/payments/agreement?ownerType=${ownerType}&ownerId=${encodeURIComponent(ownerId)}`),

  /** Accept it. The server re-renders and stores the signed PDF. */
  signFeeAgreement: ({ ownerType, ownerId, signatureName, signatureTitle, defaultFeeMode }) =>
    apiCall('/payments/agreement/sign', {
      method: 'POST',
      body: JSON.stringify({ ownerType, ownerId, signatureName, signatureTitle, defaultFeeMode }),
    }),

  /** Change who funds the fee. The agreed rate is frozen; this is not. */
  setFeeAgreementMode: ({ ownerType, ownerId, defaultFeeMode }) =>
    apiCall('/payments/agreement/fee-mode', {
      method: 'PATCH',
      body: JSON.stringify({ ownerType, ownerId, defaultFeeMode }),
    }),

  /** The signed PDF, base64, so it can be opened or downloaded. */
  getFeeAgreementDocument: (ownerType, ownerId) =>
    apiCall(`/payments/agreement/document?ownerType=${ownerType}&ownerId=${encodeURIComponent(ownerId)}`),

  // ── Statements (Kynaix raises them on the host's behalf) ────────────────

  /** The individual registrations behind a host's balance. */
  getStatementLines: (ownerType, ownerId) =>
    apiCall(`/payments/statements/lines?ownerType=${ownerType}&ownerId=${encodeURIComponent(ownerId)}`),

  listStatements: (ownerType, ownerId) =>
    apiCall(`/payments/statements?ownerType=${ownerType}&ownerId=${encodeURIComponent(ownerId)}`),

  generateStatement: (ownerType, ownerId) =>
    apiCall('/payments/statements', { method: 'POST', body: JSON.stringify({ ownerType, ownerId }) }),

  /** The stored statement document, fetched through our auth rather than a
   *  presigned link — it keeps working for whoever holds it otherwise. */
  getStatementDocument: (ownerType, ownerId, number) =>
    apiCall(`/payments/statements/document?ownerType=${ownerType}`
      + `&ownerId=${encodeURIComponent(ownerId)}&number=${encodeURIComponent(number)}`),

  giveSelfBillingConsent: (ownerType, ownerId) =>
    apiCall('/payments/self-billing-consent', { method: 'POST', body: JSON.stringify({ ownerType, ownerId }) }),

  // ── Transaction ledger ──────────────────────────────────────────────────

  /** Paginated, with a running balance computed over the whole history. */
  getLedger: ({ ownerType, ownerId, month, entity, page = 1, limit = 20 }) => {
    const q = new URLSearchParams({ ownerType, ownerId, page, limit });
    if (month) q.set('month', month);
    applyEntity(q, entity);
    return apiCall(`/payments/transactions/ledger?${q}`);
  },

  /** CSV for a date range. The server caps this at 3 months. */
  exportTransactions: ({ ownerType, ownerId, from, to, entity }) => {
    const q = new URLSearchParams({ ownerType, ownerId, from, to });
    applyEntity(q, entity);
    return apiCall(`/payments/transactions/export?${q}`);
  },

  /** Totals for the reporting view: what is owed, and income by month and event. */
  getReportSummary: ({ ownerType, ownerId, from, to }) => {
    const q = new URLSearchParams({ ownerType, ownerId });
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    return apiCall(`/payments/reports/summary?${q}`);
  },

  /** The registrations behind the "not yet collected" figure. */
  /**
   * The view-backed reports. One source behind all of them, so two tabs cannot
   * disagree about what "earned" means.
   * @param {{academyId: string, from?: string, to?: string, groupBy?: string}} p
   */
  getRevenueReport: ({ academyId, from, to, groupBy }) => {
    const q = new URLSearchParams({ academyId });
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    if (groupBy) q.set('groupBy', groupBy);
    return apiCall(`/payments/reports/revenue?${q}`);
  },

  /**
   * One offering's payments and refunds, signed, with a running balance.
   * Asked for only when a reader picks an offering — there is nothing worth
   * fetching until then.
   * @param {{academyId: string, offering: string, from?: string, to?: string, page?: number, pageSize?: number}} p
   */
  getOfferingTransactions: ({ academyId, offering, from, to, page = 1, pageSize = 10 }) => {
    const q = new URLSearchParams({ academyId, offering, page: String(page), pageSize: String(pageSize) });
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    return apiCall(`/payments/reports/offering-transactions?${q}`);
  },

  /** Booked vs billed vs collected, per offering. */
  getOfferingHealth: ({ academyId, onlyProblems = true }) =>
    apiCall(`/payments/reports/offerings?academyId=${academyId}&onlyProblems=${onlyProblems}`),

  /** Money that does not add up. An empty list is the healthy answer. */
  getReconciliation: ({ academyId }) =>
    apiCall(`/payments/reports/reconciliation?academyId=${academyId}`),

  getOutstanding: ({ ownerType, ownerId }) =>
    apiCall(`/payments/reports/outstanding?ownerType=${ownerType}&ownerId=${encodeURIComponent(ownerId)}`),

  /** Confirmation PDF for the registration behind a transaction line. */
  getRegistrationReceipt: (kind, registrationId) =>
    apiCall(`/payments/registrations/${kind}/${encodeURIComponent(registrationId)}/receipt`),

  /**
   * Ask Stripe what actually happened and settle anything it says was paid.
   * Ambiguous cases are flagged for review rather than resolved.
   */
  runAudit: (ownerType, ownerId) =>
    apiCall('/payments/audit', { method: 'POST', body: JSON.stringify({ ownerType, ownerId }) }),

  // ── Payout requests ─────────────────────────────────────────────────────

  getBillingProfile: (ownerType, ownerId) =>
    apiCall(`/payments/billing-profile?ownerType=${ownerType}&ownerId=${encodeURIComponent(ownerId)}`),

  saveBillingProfile: (payload) =>
    apiCall('/payments/billing-profile', { method: 'PUT', body: JSON.stringify(payload) }),

  listPayoutRequests: (ownerType, ownerId) =>
    apiCall(`/payments/payout-requests?ownerType=${ownerType}&ownerId=${encodeURIComponent(ownerId)}`),

  /** `full: true` withdraws the whole balance; otherwise pass `amount`. */
  createPayoutRequest: (payload) =>
    apiCall('/payments/payout-requests', { method: 'POST', body: JSON.stringify(payload) }),

  confirmPayoutInvoice: (id) =>
    apiCall(`/payments/payout-requests/${id}/confirm-invoice`, { method: 'POST' }),

  markPayoutReceived: (id) =>
    apiCall(`/payments/payout-requests/${id}/received`, { method: 'POST' }),

  cancelPayoutRequest: (id) =>
    apiCall(`/payments/payout-requests/${id}/cancel`, { method: 'POST' }),

  getPayoutInvoice: (id) =>
    apiCall(`/payments/payout-requests/${id}/invoice`),

  /** What the platform has collected for a host and not yet passed on. */
  getBalances: ({ hostType, hostId } = {}) => {
    const q = new URLSearchParams();
    if (hostType) q.set('hostType', hostType);
    if (hostId) q.set('hostId', hostId);
    return apiCall(`/payments/balances${q.toString() ? `?${q}` : ''}`);
  },
};

export default paymentsAPI;
